import type { Locale } from '@whizz/react-branding/dist/@types/types';
import type { MenuItemProps } from '../../components/menu/types';
import type { TeachersResourceType } from '../../util/types';
import type { Teacher } from '@whizz/api-model';
import type { Curriculums } from '../../util/vars';
import React, { useState } from 'react';
import { Translate, useI18n } from '@whizz/react-i18n';
import { model } from '@whizz/api-client';
import { MathsWhizzLogo } from '@whizz/react-branding';
import { openLiveChat } from '@whizz/react-acquire';
import { Menu } from '../../components';
import { CURRICULUM_REPORTS, GLOBAL_ICONS, QUERIES_IDS, ROUTES, TEACHERS_RESOURCES } from '../../util/vars';
import { DEFAULT_LOCALE_ID } from '../../routes/App';
import { useIdentity } from '@whizz/react-identity';
import { useQuery } from 'react-query';
import { useAdminModal } from '../../global/modals/AdminModal';
import classNames from 'classnames';

type TeachersResourceCountry = keyof typeof TEACHERS_RESOURCES;

const Header = ({ children, hideMenu = false, classId }: { children: React.ReactNode, hideMenu?: boolean, classId?: number }): JSX.Element => {
  const { me } = useIdentity();
  const teacherId = (me as Teacher)?.getId();
  const isAdmin = (me as Teacher)?.getIsAdmin();
  const hook = useI18n();

  const [ adminModal, toggleAdminModal ] = useAdminModal();

  let locale = new model.Locale({ id: DEFAULT_LOCALE_ID });
  if (null !== hook.locale) {
    // Fallback to default. LocaleNotFound draw the Header component
    locale = hook.locale;
  }

  const [ open, setOpen ] = useState(false);

  const teacher = useQuery(
    [ teacherId ],
    () => model.Teacher.one(teacherId!),
    {
      enabled: !hideMenu && !!teacherId,
    },
  );

  const subscribedProducts = useQuery<{ country: string, version: string }[] | null>(
    [ QUERIES_IDS.SUBSCRIBED_PRODUCTS ],
    () => {
      if (teacher.data?.school_id) {
        return model.School.getSubscribedProducts(teacher.data?.school_id);
      }
      return null;
    },
    {
      enabled: !!teacher.data?.school_id,
    },
  );

  const school = useQuery(
    [ teacher.data?.school_id ],

    () => {
      if (teacher.data?.school_id) {
        return model.School.get(teacher.data?.school_id);
      }
      return null;
    },
    {
      enabled: !!teacher.data?.school_id,
    },
  );

  const teachersResourceLinks = (): MenuItemProps | null => {

    const trProducts = subscribedProducts.data;
    const getTR = (trLocale: TeachersResourceType, multiple: boolean): MenuItemProps => {
      const { locale, flag } = trLocale;
      return ({
        id: `teachers_resource-${locale}`,
        icon: multiple ? { iso2: (flag ?? locale).split('-')[1] } : undefined,
        label: multiple ? hook.translate(`teachers_resource.${locale}`) : hook.translate('global.ROUTES.TEACHERS_RESOURCE'),
        type: 'a',
        other: {
          href: `${ROUTES.teachers_resource}?locale=${locale}`,
          target: '_blank',
        },
      });
    };

    const trCountryVersion = trProducts?.length ? TEACHERS_RESOURCES[trProducts[0].country as TeachersResourceCountry] : null;
    if (trCountryVersion) {
      const trArray: MenuItemProps[] = trCountryVersion.map((tr) => getTR(tr, trCountryVersion.length > 1));
      if (trArray.length > 1) {
        return ({
          id: 'teachers_resource_multi',
          label: hook.translate('global.ROUTES.TEACHERS_RESOURCE'),
          children: trArray,
        });
      } else {
        return trArray[0];
      }
    } else if (subscribedProducts.isFetched) {
      return ({
        id: 'no_teachers_resource',
        label: hook.translate('global.ROUTES.TEACHERS_RESOURCE'),
        children: [ {
          id: 'disabled_tr',
          label: (
            <div>
              <Translate i18nKey={'global.TEACHERS_RESOURCE_UPGRADE.MESSAGE'}>
                {/**
                 * eslint-disable-next-line @typescript-eslint/ban-ts-comment
                 * @ts-ignore **/ }
                <a href={`mailto:${hook.translate('global.SUPPORT_EMAIL')}?subject=${hook.translate('global.TEACHERS_RESOURCE_UPGRADE.SUBJECT')}`} className={'underline'} target={'_blank'} />
              </Translate>
            </div>
          ),
          type: 'div',
        } ],
      });
    }
    return ({
      id: 'loading_teachers_resource',
      label: hook.translate('global.ROUTES.TEACHERS_RESOURCE'),
      other: {
        disabled: true,
        waiting: true,
      },
    });
  };

  const getLinks = (): MenuItemProps | null => {
    if (!me) {
      return null;
    }
    const trProducts = subscribedProducts.data;
    const curriclumReportLink = (): MenuItemProps | null => {
      const getCurriculumLink = (curriculum: Curriculums, multiple = false): MenuItemProps => {
        return (
          {
            id: `curriculum_report-${curriculum}`,
            type: 'a',
            label: multiple ? hook.translate(`curriculum_reports.${curriculum}`) : hook.translate('global.ROUTES.CURRICULUM_REPORT'),
            other: {
              href: `${ROUTES.curriculum_report}?newui=1&school-id=${(me as Teacher).getSchoolId()}&class-id=${classId}&curriculum=${curriculum}`,
              target: '_blank',
            },
          }
        );
      };

      const curriculums = CURRICULUM_REPORTS[(trProducts?.length ? trProducts[0].country : (me as Teacher).country) as TeachersResourceCountry];
      if (curriculums && classId) {
        const curriculumLink: MenuItemProps[] = curriculums.map((curriculum) => getCurriculumLink(curriculum, curriculums.length > 1));
        if (curriculumLink.length > 1) {
          return ({
            id: 'curriculum_report_multi',
            label: hook.translate('global.ROUTES.CURRICULUM_REPORT'),
            children: curriculumLink,
          });
        } else {
          return curriculumLink[0];
        }
      }
      return null;
    };

    const children = [
      curriclumReportLink(),

    ].filter((a) => a !== null) as MenuItemProps[];

    return ({
      id: 'links',
      label: hook.translate('global.ROUTES.LINKS'),
      children,
    });
  };

  const getCurriculumLinks = (): MenuItemProps[] => {
    if (!me) {
      return [];
    }
    const trProducts = subscribedProducts.data;

    const getCurriculumLink = (curriculum: Curriculums, multiple = false): MenuItemProps => {
      return (
        {
          id: `curriculum_report-${curriculum}`,
          type: 'a',
          label: multiple ? hook.translate(`curriculum_reports.${curriculum}`) : hook.translate('global.ROUTES.CURRICULUM_REPORT'),
          other: {
            href: `${ROUTES.curriculum_report}?newui=1&school-id=${(me as Teacher).getSchoolId()}&class-id=${classId}&curriculum=${curriculum}`,
            target: '_blank',
          },
        }
      );
    };

    const curriculums = CURRICULUM_REPORTS[(trProducts?.length ? trProducts[0].country : (me as Teacher).country) as TeachersResourceCountry];
    if (curriculums && classId) {
      const curriculumLink: MenuItemProps[] = curriculums.map((curriculum) => getCurriculumLink(curriculum, curriculums.length > 1));
      if (curriculumLink.length > 1) {
        return [ {
          id: 'curriculum_report_multi',
          label: hook.translate('global.ROUTES.CURRICULUM_REPORT'),
          children: curriculumLink,
        } ].filter((a) => a !== null) as MenuItemProps[];
      } else {
        return curriculumLink;
      }
    }
    return [] as MenuItemProps[];

  };

  const getRosterManagement = (): MenuItemProps => {
    if (isAdmin) {
      return ({
        id: 'roster_management',
        type: 'a',
        label: hook.translate('global.ROUTES.ROSTER_MANAGEMENT'),
        other: {
          href: ROUTES.roster_management,
        },
      });
    } else {
      return ({
        id: 'roster_management',
        label: hook.translate('global.ROUTES.ROSTER_MANAGEMENT'),
        children: [ {
          id: 'disabled_rm',
          label: (
            <div onClick={() => {
              setOpen(false);
              toggleAdminModal(true);
            }} className={'cursor-pointer'}>{hook.translate('global.NO_ROSTER_MANAGEMENT.GET_ACCESS')}</div>
          ),
          type: 'div',
        } ],
      });
    }
  };

  const menuItems = (): MenuItemProps[] => ([
    {
      id: 'home',
      type: 'a',
      label: hook.translate('global.ROUTES.HOME'),
      exactMatch: true,
      other: {
        href: ROUTES.home,
      },
    },
    teachersResourceLinks() as MenuItemProps,
    {
      id: 'reports',
      label: hook.translate('global.ROUTES.REPORTS'),
      children: [
        {
          id: 'class_reports',
          type: 'a',
          label: hook.translate('global.ROUTES.CLASS_REPORT'),
          other: {
            href: ROUTES.reports,
          },
        },
        {
          id: 'school_reports',
          type: 'a',
          label: hook.translate('global.ROUTES.SCHOOL_REPORT'),
          other: {
            href: ROUTES.school_reports,
          },
          hidden: !isAdmin,
        },
        ...getCurriculumLinks(),
        {
          id: 'lesson_history',
          type: 'a',
          label: hook.translate('global.ROUTES.LESSON_HISTORY'),
          other: {
            href: ROUTES.lesson_history,
          },
        },
        {
          id: 'topic_focus',
          type: 'a',
          label: hook.translate('global.ROUTES.TOPIC_FOCUS'),
          other: {
            href: ROUTES.topic_focus,
          },
        },
      ].filter((item) => item.hidden !== true),
    },
    {
      id: 'tools',
      label: hook.translate('global.ROUTES.TOOLS'),
      children: [
        {
          id: 'reset_assessments',
          type: 'a',
          label: hook.translate('global.ROUTES.RESET_ASSESSMENTS'),
          other: {
            href: ROUTES.reset_assessments,
          },
        },
        {
          id: 'certificate',
          type: 'a',
          label: hook.translate('global.ROUTES.CERTIFICATE'),
          other: {
            href: `${ROUTES.certificate}?locale=${locale.id}`,
            target: '_blank',
          },
        },
        {
          id: 'messages',
          type: 'a',
          label: hook.translate('global.ROUTES.MESSAGES'),
          other: {
            href: ROUTES.messages,
          },
        },
      ],
    },
    getRosterManagement() as MenuItemProps,
    {
      id: 'chat',
      type: 'button',
      label: hook.translate('global.ROUTES.LIVE_CHAT'),
      other: {
        onClick: () => openLiveChat(),
      },
    },
    {
      id: 'logout',
      type: 'a',
      icon: GLOBAL_ICONS.logout,
      label: hook.translate('global.ROUTES.LOGOUT'),
      other: {
        href: ROUTES.logout,
      },
    },
  ].filter((item) => item !== null) as MenuItemProps[]);

  const schoolName = school.data?.name;
  const isLongSchoolName = schoolName ? schoolName.length > 20 : false;

  return (
    <>
      {adminModal}
      <div className={'relative bg-header w-full'}>
        <div className={'flex items-center justify-between mx-md py-md'}>
          <div className={'flex flex-1 items-center'}>
            <div className={`hidden w-44 flex-none ${classNames({ 'xl:block': isLongSchoolName, 'lg:block': !isLongSchoolName })}`}>
              <MathsWhizzLogo locale={locale.getId() as Locale} tutoringPlusByline={false} data-cy="header-big-logo" />
            </div>
            <div className={`block w-10 flex-none ${classNames({ 'xl:hidden': isLongSchoolName, 'lg:hidden': !isLongSchoolName })}`}>
              <MathsWhizzLogo locale={locale.getId() as Locale} disableText disableBlue tutoringPlusByline={false} data-cy="header-small-logo" />
            </div>
            {schoolName ? <div className={'hidden md:flex self-stretch my-1 items-center ml-4 gap-2'}>
              <div className={'w-0.1 self-stretch bg-green-700 bg-opacity-50'}></div>
              <h2 className={`text-sm text-primary ${classNames({ 'xl:text-xl': isLongSchoolName, 'lg:text-xl': !isLongSchoolName })}`}>{schoolName}</h2>
            </div> : null}
          </div>
          <div className={'mx-md flex-5 md:flex-1'}>
            {children}
          </div>
          {!hideMenu && (
            <div className={'flex flex-1 justify-end'}>
              <Menu
                items={menuItems()}
                open={open}
                handleClick={() => setOpen(!open)}
              />
            </div>)
          }
        </div>
      </div>
    </>
  );
};

Header.displayName = 'Header';

export default Header;
